import React, { useContext, useEffect, useState } from "react";
import { Button, Switch, message, Modal } from "antd";
import {  EyeOutlined, StopOutlined, EditOutlined, ExperimentOutlined, UploadOutlined, FileSearchOutlined } from "@ant-design/icons";
import { FlexibleTable } from "../flexibleTable/flexibleTable.component";
import FilterDropdownType from '../../enums/filterDropdownType.enum.js';
import moment from "moment";
import { useNavigate } from 'react-router-dom';
import { listResults, updateResult, removeResult, uploadCsvFile, extractTestData } from "../../services/result.service";
import UrlHelper from '../../helpers/url.helper.js';
import { PageHeader } from "../pageHeader/pageHeader.component";
import { TestResultsModal } from "../testResultsModal/testResultsModal.component";
import ResultStatus from "../../enums/resultStatus.enum.js";
import Permission from "../../enums/permission.enum.js";
import Role from "../../enums/role.enum.js";
import { UserContext } from "../../contexts/user.context.js";
import { IncludedTestsModal } from "../includedTestsModal/includedTestsModal.component.js";
import ResultHelper from "../../helpers/result.helper.js";
import { IncompleteTestTooltip } from "../incompleteTestTooltip/incompleteTestTooltip.component.js";
import "./adminResults.scss";
import TestPanelModal from "../testPanelModal/testPanelModal.component.js";

const select = '_id status patient order hasRedraw fasting collectedAt values tests'
const populate = [{
  path: 'patient',
  select: '_id firstName lastName'
  }, {
  path: 'order',
  select: 'panels tests',
  populate: [{
    path: 'panels',
    select: '_id name tests'
  }, {
    path: 'tests',
    select: '_id name isCalculated'
  }]
}, {
  path: 'tests',
  select: '_id name isCalculated'
}]

export const AdminResults = () => {
  const [results, setResults] = useState([]);
  const [resultId, setResultId] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [openIncludedTests, setOpenIncludedTests] = useState()
  const { currentUser, setCounts } = useContext(UserContext)
  const navigate = useNavigate()
  const [isTestResultsModalOpen, setIsTestResultsModalOpen] = useState();
  const [filteredCount, setFilteredCount] = useState();

  const [selectedTests, setSelectedTests] = useState([]);
  const [isTestPanelModalOpen, setIsTestPanelModalOpen] = useState(false);

  useEffect(() => {
    document.title = '[Admin] Test Results'
  }, []);

  const fetchResults = async (page = 1) => {
    let params = {
      select,
      populate,
      sort: '-collectedAt',
      // annotate: true,
      ignorePreviousResults: true,
      page,
      limit: 100,
      filter: {
        collectedAt: {
          $lte: moment().toDate()
        }
      }
    }

    if (currentUser?.role === Role.PROVIDER) {
      params.annotate = true
    }

  

    try {
      const fetchedResults = await listResults(params);
      
      if (!fetchedResults || !Array.isArray(fetchedResults)) {
        throw new Error('Invalid response from listResults');
      }

      setResults(prevResults => [...prevResults, ...fetchedResults]);
      setFilteredCount(prevCount => prevCount + fetchedResults.length);
      return fetchedResults.length;
    } catch (error) {
      message.error('Failed to load results');
      console.error(error);
      return 0;
    }
  };

  useEffect(() => {
    const loadAllResults = async () => {
      setResults([]); // Clear existing results
      setFilteredCount(0); // Reset count
      setIsLoading(true); // Start loading

      let currentPage = 1;
      let hasMoreResults = true;

      while (hasMoreResults) {
        const fetchedCount = await fetchResults(currentPage);
        if (fetchedCount === 0) {
          hasMoreResults = false;
          setHasMore(false);
        }
        currentPage++;
        setCurrentPage(currentPage);
      }

      setIsLoading(false);
    };

    loadAllResults();
  }, []); // Empty dependency array for initial load only

  const onIncludedTestsSuccess = (result) => {
    setResults(cachedResults => {
      return cachedResults.map(r => r._id === result._id ? result : r)
    })
  }

  const getCustomFilter = (result, value) => {
    return {
      patient: () => result.patient && `${result.patient.firstName} ${result.patient.lastName}`.toLowerCase().includes(value.toLowerCase()),
      order: () => result.order?.panel  && result.order?.panel.toLowerCase().includes(value.toLowerCase())
    }
  }

  const confirmDelete = (resultId) => {
    Modal.confirm({
      title: "Are you sure you want to delete this result?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No, Cancel",
      onOk: async () => {
        try {
          await removeResult(resultId);
          setResults(results => results.filter(r => r._id !== resultId));
          setFilteredCount(filteredCount - 1);
          message.success("Result deleted successfully");
        } catch (error) {
          message.error("Failed to delete result");
        }
      },
    });
  };

  const extractTestResults = (resultId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
  
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (file) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const response = await extractTestData(resultId, formData);
          if (response) {
            message.success('File uploaded successfully');
            setResults(prevResults => 
              prevResults.map(result => 
                result._id === resultId ? response : result
              )
            );
          } else {
            message.error('Failed to upload file');
          }
        } catch (error) {
          message.error('Error uploading file');
        }
      }
    };
  
    input.click();
  };

  const handleFileUpload = (resultId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
  
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (file) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const response = await uploadCsvFile(resultId, formData);
          if (response.success) {
            message.success('File uploaded successfully');
            setResults(prevResults => 
              prevResults.map(result => 
                result._id === resultId ? response.result : result
              )
            );
          } else {
            message.error('Failed to upload file');
          }
        } catch (error) {
          message.error('Error uploading file');
        }
      }
    };
  
    input.click();
  };

  const getActionItems = (result) => {
    const isAdmin = currentUser?.role === Role.ADMIN

    return [{
      key: 'view',
      label: (
        <Button onClick={() => {
          navigate(UrlHelper.getPatientProfile(result.patient?._id, 'Results'))
        }}>
          <EyeOutlined /> View
        </Button>
      )
    }, 
    
    isAdmin && {
      key: 'upload',
      label: (
        <Button
          icon={<UploadOutlined />}
          onClick={() => handleFileUpload(result._id)} // Handle file upload
        >
          Upload CSV
        </Button>
      )
    },
    
    isAdmin && {
      key: 'edit',
      label: (
        <Button
          onClick={() => {
            setResultId(result._id);
            setIsTestResultsModalOpen(true);
          }}
        >
          <EditOutlined /> Edit
        </Button>
      )
    }, isAdmin && {
      key: 'tests',
      label: (
        <Button
          onClick={() => {
            setResultId(result._id);
            setOpenIncludedTests(true);
          }}
        >
          <ExperimentOutlined /> Included Tests
        </Button>
      )
    }, 
    isAdmin && {
      type: 'divider'
    },
      isAdmin && {
      key: 'delete',
      label: (
        <Button
          danger
          onClick={() => confirmDelete(result._id)}
        >
          <StopOutlined /> Delete
        </Button>
      )
    }]
  }

  const getRowClassName = (record) => {
    return isIncompleteAndPastDue(record) ? 'incomplete-past-due' : ''

  }

  const isIncompleteAndPastDue = (record) => {
    if (currentUser?.role === Role.ADMIN && record.tests?.length) {
      const { percentageComplete } = ResultHelper.getProgressData(record, true)
      const isPastDue = moment(record.collectedAt).isBefore(moment())
      return percentageComplete < 100 && isPastDue
    }
    return false
  }

  return (
    <div className="admin-results">

      <TestPanelModal
        visible={isTestPanelModalOpen}
        onClose={() => setIsTestPanelModalOpen(false)}
        testDetails={selectedTests}
        readOnly
      />
      <PageHeader
        title="Test Results"
        count={filteredCount}
        actions={currentUser?.role === Role.ADMIN && (
          <Button
            type="primary"
            onClick={() => {
              setResultId(null);
              setIsTestResultsModalOpen(true);
            }}
          >
            + Add Result
          </Button>
        )}
      />

      <IncludedTestsModal
        resultId={resultId}
        open={openIncludedTests}
        setOpen={setOpenIncludedTests}
        onSuccess={onIncludedTestsSuccess}
        select={select}
        populate={populate}
      />

      <TestResultsModal
        resultId={resultId}
        setResultId={setResultId}
        open={isTestResultsModalOpen}
        setOpen={setIsTestResultsModalOpen}
        select={select}
        populate={populate}
        onSuccess={(result) => {
          if (results.some(r => r._id === result._id)) {
            setResults(results.map((p) => (p._id === resultId ? result : p)));
          } else {
            setResults([result, ...results]);
            setCounts(cachedCounts => {
              return {
                ...cachedCounts,
                results: cachedCounts.results + 1
              }
            })
            setFilteredCount(cachedFilterCount => cachedFilterCount+1)
          }
        }}
      />

      <FlexibleTable
        pageSize={30}
        isLoading={isLoading}
        records={results.sort((a, b) => {
          // Bubble incomplete and past due records to the top
          const aIncomplete = isIncompleteAndPastDue(a)
          const bIncomplete = isIncompleteAndPastDue(b)
          if (aIncomplete && !bIncomplete) return -1
          if (!aIncomplete && bIncomplete) return 1
          // If both are incomplete or both are complete, sort by collection date
          return moment(b.collectedAt).valueOf() - moment(a.collectedAt).valueOf()
        })}
        setFilteredCount={setFilteredCount}
        getCustomFilter={getCustomFilter}
        getActionItems={getActionItems}
        rowClassName={getRowClassName}

        columns={[currentUser?.role === Role.ADMIN && {
          title: "Approved",
          dataIndex: "status",
          render: (status, { _id }) => {
            return (
              <Switch
                defaultChecked={status === ResultStatus.APPROVED}
                onChange={async (checked) => {
                  const result = await updateResult(_id, {
                    fields: {
                      status: checked ? ResultStatus.APPROVED : ResultStatus.PENDING,
                      approvedAt: checked ? new Date() : null
                    },
                    select,
                    populate
                  });
                  setResults(results.map((r) => r._id === result._id ? result : r))
                  message.info(
                    result.status === ResultStatus.PENDING
                      ? "Approval removed"
                      : "Result approved"
                  );
                }}
              />
            )
          },
          filterDropdownType: FilterDropdownType.CHECKBOX,
          filterOptions: Object.values(ResultStatus)
        }, {
          title: 'Patient',
          dataIndex: 'patient',
          render: patient => {
            if (patient) {
              return (
                <a 
                  onClick={() => window.open(UrlHelper.getPatientProfile(patient._id, 'Results'))}
                  target="_blank"
                >
                  {patient.firstName} {patient.lastName}
                </a>
              )
            }
          },
          filterDropdownType: FilterDropdownType.INPUT
        }, 

        {
          title: 'Tests Ordered',
          dataIndex: 'order',
          render: (order) => {
            return (
              <>
                {order?.panels?.length > 0 && (
                  <>
                    {order.panels.map(panel => panel.name).join(", ")}
                  </>
                )}

                {order?.tests?.length > 0 && (
                  <Button 
                    type="link" 
                    className="secondary-link"
                    onClick={() => {
                      setSelectedTests(order.tests);
                      setIsTestPanelModalOpen(true);
                    }}
                  >
                    + {order.tests.length} test{order.tests.length > 1 ? 's' : ''}
                  </Button>
                )}

                {(!order || (order.tests?.length === 0 && order.panels?.length === 0)) && <>-</>}
              </>
            )
          }
        },
        // {
        //   title: 'Panel',
        //   dataIndex: 'order',
        //   render: (order) => order?.panel ? <>{order?.panel}</> : <>Custom</>,
        //   filterDropdownType: FilterDropdownType.INPUT,
        // }, 
        
        // currentUser?.role === Role.ADMIN && {
        //   title: "Redraw",
        //   dataIndex: "hasRedraw",
        //   render: hasRedraw => {
        //     return hasRedraw ? (
        //       <CheckCircleOutlined style={{ color: Color.success }} />
        //     ) : (
        //       <StopOutlined style={{ color: Color.error }} />
        //     )
        //   },
        //   filterDropdownType: FilterDropdownType.CHECKBOX,
        //   filterOptions: booleanOptions
        // }, 
        
        (currentUser?.role === Role.ADMIN || currentUser?.permissions?.includes(Permission.RESULT_PROGRESS)) && {
          title: '% Complete',
          dataIndex: 'tests',
          render: (tests, result) => {
            if (!tests?.length) return
            const { percentageComplete } = ResultHelper.getProgressData(result)
            return (
              <IncompleteTestTooltip 
                result={result} 
                label={`${percentageComplete}%`}
              />
            )
          }
        }, 

        (currentUser?.role === Role.ADMIN) && {
          title: '# Tests Returned',
          dataIndex: 'result',
          render: (_, result) => result?.values?.length
        }, 
        // {
        //   title: "Fasting",
        //   dataIndex: "fasting",
        //   render: (fasting) => {
        //     return fasting ? (
        //       <CheckCircleOutlined style={{ color: Color.success }} />
        //     ) : (
        //       <StopOutlined style={{ color: Color.error }} />
        //     )
        //   },
        //   filterDropdownType: FilterDropdownType.CHECKBOX,
        //   filterOptions: booleanOptions
        // }, 
        {
          title: "Collected",
          dataIndex: "collectedAt",
          render: (collectedAt) => moment(collectedAt).format("MM/DD/YY h:mm a")
        },
        currentUser?.role === Role.ADMIN && {
          title: "",
          dataIndex: "upload",
          render: (_, result) => (
            <>
            <Button
              icon={<UploadOutlined />}
              onClick={() => handleFileUpload(result._id)}
              style={{ marginRight: 4 }}
            >
            </Button>
            <Button
            icon={<FileSearchOutlined />}
            onClick={() => extractTestResults(result._id)}
          >
          </Button>
          </>
          )
        }
      ]}
      />
    </div>
  );
};
